import { useCallback } from "react";
import type { SpaceDescriptor } from "@mind-sands/shared";
import { useSceneStore } from "../stores/sceneStore";
import { useRecompileScene } from "./useRecompileScene";

type SpaceSemanticFieldUpdates = Partial<
  Pick<
    SpaceDescriptor,
    "terrain" | "sky" | "conditions" | "medium" | "timeOfDay" | "soundtracks"
  >
>;

type SpaceListingFieldUpdates = Partial<
  Pick<SpaceDescriptor, "category" | "description">
>;

/** Semantic space editing — updates SpaceDescriptor fields and recompiles the scene */
export function useSpaceEditor() {
  const activeSpace = useSceneStore((s) => s.activeSpaceDescriptor);
  const updateSpaceField = useSceneStore((s) => s.updateSpaceField);
  const recompileScene = useRecompileScene();

  const updateField = useCallback(
    async (updates: SpaceSemanticFieldUpdates) => {
      updateSpaceField(updates);
      await recompileScene();
    },
    [updateSpaceField, recompileScene],
  );

  /** Gallery listing fields — persisted on save, no scene recompile needed. */
  const updateListingField = useCallback(
    (updates: SpaceListingFieldUpdates) => {
      updateSpaceField(updates);
    },
    [updateSpaceField],
  );

  return { activeSpace, updateField, updateListingField };
}
